import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { useViewportSize } from '@/hooks/useViewportSize';
import { ArrowUpRight, BookOpen, FileText, MessageSquare, Plus, Search, Trash2 } from 'lucide-react';
import MatterWorkspaceContext, { withQueryParams } from '@/components/matter/MatterWorkspaceContext';
import './DocumentsPage.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPES = ['All', 'NDA', 'CONTRACT', 'AFFIDAVIT', 'PLAINT', 'PETITION', 'LEGAL_NOTICE', 'BAIL_APPLICATION', 'RENT_AGREEMENT', 'CUSTOM'];
const STATUS_ORDER = ['DRAFT', 'FINAL', 'SHARED', 'ARCHIVED'];

const STATUS_META = {
  All: {
    label: 'All work',
    sectionTitle: 'Complete desk',
    description: 'Every draft, filing copy, and archived version on the Vakalat desk.',
  },
  DRAFT: {
    label: 'In progress',
    sectionTitle: 'Live drafting',
    description: 'Pleadings, notices, and working papers still moving through chamber.',
  },
  FINAL: {
    label: 'Finalised',
    sectionTitle: 'Finalised files',
    description: 'Settled documents that are ready to circulate, file, or reuse.',
  },
  SHARED: {
    label: 'Shared',
    sectionTitle: 'Shared outside chamber',
    description: 'Documents already sent onward for review, filing, or client circulation.',
  },
  ARCHIVED: {
    label: 'Archive',
    sectionTitle: 'Archive',
    description: 'Older drafts retained for precedent value, history, or recovery.',
  },
};

function formatDocType(type) {
  return String(type || 'CUSTOM')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatShortDate(value) {
  if (!value) return 'No activity yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No activity yet';
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

function formatRelativeDate(value) {
  if (!value) return 'Pending activity';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Pending activity';
  const diffMs = Date.now() - date.getTime();
  const diffHours = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60)));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 1) return 'Moments ago';
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return formatShortDate(value);
}

function getWordCount(doc) {
  if (Number.isFinite(doc?.word_count)) return doc.word_count;
  const content = String(doc?.content || '').trim();
  if (!content) return 0;
  return content.split(/\s+/).filter(Boolean).length;
}

function getPreview(doc) {
  const content = String(doc?.content || '').replace(/\s+/g, ' ').trim();
  if (!content) {
    if (doc?.court) return `${formatDocType(doc.type)} aligned for ${doc.court}.`;
    return `${formatDocType(doc?.type)} draft ready for chamber work.`;
  }
  return content.length > 180 ? `${content.slice(0, 177)}...` : content;
}

function buildGroupedSections(documents, statusFilter) {
  const keys = statusFilter === 'All' ? STATUS_ORDER : [statusFilter];
  return keys
    .map((status) => ({
      key: status,
      ...STATUS_META[status],
      items: documents.filter((doc) => doc.status === status),
    }))
    .filter((section) => section.items.length > 0);
}

export default function DocumentsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAuthHeaders } = useAuth();
  const { width, height } = useViewportSize();
  const searchParams = new URLSearchParams(location.search);
  const matterId = searchParams.get('matter_id') || '';
  const matterName = searchParams.get('matter_name') || matterId;
  const [documents, setDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [showNewModal, setShowNewModal] = useState(false);
  const [newDocType, setNewDocType] = useState('CUSTOM');
  const [newDocTitle, setNewDocTitle] = useState('');
  const [generating, setGenerating] = useState(false);
  const [landingExpanded, setLandingExpanded] = useState(false);

  const fetchDocuments = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/documents`, { headers: getAuthHeaders(), credentials: 'include' });
      if (resp.ok) setDocuments(await resp.json());
    } catch {}
  }, [getAuthHeaders]);

  useEffect(() => {
    document.title = 'Drafts — Advoq';
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const openDocument = (docId) => navigate(withQueryParams(`/documents/${docId}`, {
    matter_id: matterId,
    matter_name: matterName,
  }));

  const openLinkedWorkspace = (doc, route) => {
    const params = new URLSearchParams({
      linked_doc_id: doc.doc_id,
      linked_doc_type: 'document',
      linked_doc_title: doc.title,
    });
    if (matterId || matterName) {
      params.set('matter_id', matterId || matterName);
      params.set('matter_name', matterName || matterId);
    }
    navigate(`${route}?${params.toString()}`);
  };

  const createDocument = async () => {
    if (!newDocTitle.trim()) return;
    try {
      const resp = await fetch(`${API}/documents`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ title: newDocTitle, type: newDocType, content: '' }),
      });
      if (resp.ok) {
        const doc = await resp.json();
        toast.success('Draft created');
        setShowNewModal(false);
        setNewDocTitle('');
        openDocument(doc.doc_id);
      }
    } catch {
      toast.error('Failed to create draft');
    }
  };

  const generateDocument = async () => {
    setGenerating(true);
    try {
      const resp = await fetch(`${API}/documents/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ type: newDocType, user_inputs: newDocTitle }),
      });
      if (resp.ok) {
        const doc = await resp.json();
        toast.success('First draft generated');
        setShowNewModal(false);
        setNewDocTitle('');
        openDocument(doc.doc_id);
      }
    } catch {
      toast.error('Failed to generate draft');
    } finally {
      setGenerating(false);
    }
  };

  const deleteDocument = async (docId) => {
    try {
      await fetch(`${API}/documents/${docId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      setDocuments((prev) => prev.filter((doc) => doc.doc_id !== docId));
      toast.success('Draft deleted');
    } catch {}
  };

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filtered = documents.filter((doc) => {
    const searchableText = [
      doc.title,
      doc.type,
      formatDocType(doc.type),
      doc.court,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (normalizedSearch && !searchableText.includes(normalizedSearch)) return false;
    if (typeFilter !== 'All' && doc.type !== typeFilter) return false;
    if (statusFilter !== 'All' && doc.status !== statusFilter) return false;
    return true;
  });

  const groupedSections = buildGroupedSections(filtered, statusFilter);
  const totalDocuments = documents.length;
  const draftCount = documents.filter((doc) => doc.status === 'DRAFT').length;
  const finalCount = documents.filter((doc) => doc.status === 'FINAL').length;
  const sharedCount = documents.filter((doc) => doc.status === 'SHARED').length;
  const archivedCount = documents.filter((doc) => doc.status === 'ARCHIVED').length;
  const activeCollection = STATUS_META[statusFilter];
  const collectionItems = [
    { key: 'All', count: totalDocuments },
    ...STATUS_ORDER.map((status) => ({
      key: status,
      count: documents.filter((doc) => doc.status === status).length,
    })),
  ];
  const isCompactViewport = width < 1100 || height < 900;
  const isLandingCollapsed = !landingExpanded && !searchQuery.trim() && typeFilter === 'All' && statusFilter === 'All' && !showNewModal;
  const landingCollectionLimit = isCompactViewport ? 3 : collectionItems.length;
  const landingSectionLimit = 1;
  const landingRowLimit = isCompactViewport ? 1 : 2;
  const visibleCollectionItems = isLandingCollapsed ? collectionItems.slice(0, landingCollectionLimit) : collectionItems;
  const visibleGroupedSections = isLandingCollapsed ? groupedSections.slice(0, landingSectionLimit) : groupedSections;
  const visibleDocumentCount = visibleGroupedSections.reduce((count, section) => count + (isLandingCollapsed ? section.items.slice(0, landingRowLimit).length : section.items.length), 0);
  const hiddenCollectionCount = Math.max(0, collectionItems.length - visibleCollectionItems.length);
  const hiddenDocumentCount = Math.max(0, filtered.length - visibleDocumentCount);
  const expandLanding = () => setLandingExpanded(true);
  const matterContextItems = matterId ? [
    { key: 'overview', onClick: () => navigate(`/war-room/${matterId}`) },
    { key: 'draft', badge: draftCount ? String(draftCount) : '', onClick: () => navigate(withQueryParams('/documents', { matter_id: matterId, matter_name: matterName })) },
    { key: 'files', onClick: () => navigate(withQueryParams('/research', { matter_id: matterId, matter_name: matterName, context_panel: 'files' })) },
    { key: 'jolly', onClick: () => navigate(withQueryParams('/jolly', { matter_id: matterId, matter_name: matterName })) },
    { key: 'review', onClick: () => navigate(`/war-room/${matterId}?view=agents`) },
  ] : [];

  return (
    <div style={{ display: 'contents' }}>
      <main className={`app-main vakalat-page-shell ${isLandingCollapsed ? 'vakalat-page-shell--collapsed' : 'vakalat-page-shell--expanded'}`} data-testid="documents-page">
        <div className="vakalat-page">
          {matterId ? (
            <MatterWorkspaceContext
              matterName={matterName}
              matterId={matterId}
              objective="Continue drafting from the same matter context."
              items={matterContextItems}
              activeKey="draft"
              compact
              caption="Matter context"
              testId="matter-workspace-context"
            />
          ) : null}

          <section className="app-shell-panel vakalat-hero">
            <div className="vakalat-hero__header">
              <div>
                <p className="vakalat-eyebrow">Drafts</p>
                <h1 className="vakalat-hero__title">Draft workspace</h1>
                <p className="vakalat-hero__description">
                  Matter-linked drafting, filing copies, and precedent-ready work in one calm desk.
                </p>
              </div>

              <div className="vakalat-hero__actions">
                <button type="button" onClick={() => { expandLanding(); navigate('/templates'); }} className="advoq-btn-secondary vakalat-toolbar__button">
                  <BookOpen size={14} />
                  Browse Precedents
                </button>
                <button
                  type="button"
                  data-testid="new-document-btn"
                  onClick={() => {
                    expandLanding();
                    setShowNewModal(true);
                  }}
                  className="advoq-btn-primary vakalat-toolbar__button"
                >
                  <Plus size={14} />
                  New Draft
                </button>
              </div>
            </div>

            <div className="vakalat-hero__stats">
              {[
                { label: 'Live drafting', value: draftCount, note: 'Working papers still moving through chamber.' },
                { label: 'Finalised', value: finalCount, note: 'Settled copies ready for filing or circulation.' },
                { label: 'Shared', value: sharedCount, note: 'Documents already moving outside the chamber.' },
                { label: 'Archive', value: archivedCount, note: 'Reference drafts retained for precedent value.' },
              ].map((item) => (
                <article key={item.label} className="vakalat-hero__stat">
                  <span className="vakalat-hero__stat-label">{item.label}</span>
                  <strong className="vakalat-hero__stat-value">{item.value}</strong>
                  <p className="vakalat-hero__stat-note">{item.note}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="app-shell-panel vakalat-toolbar">
            <div className="vakalat-toolbar__top">
              <div className="vakalat-search">
                <Search size={15} />
                <input
                  data-testid="documents-search"
                  value={searchQuery}
                  onFocus={expandLanding}
                  onChange={(event) => {
                    setSearchQuery(event.target.value);
                    expandLanding();
                  }}
                  className="advoq-input vakalat-search__input"
                  placeholder="Search filings, courts, or matter-linked drafts"
                />
              </div>

              <select
                data-testid="type-filter"
                value={typeFilter}
                onChange={(event) => {
                  setTypeFilter(event.target.value);
                  expandLanding();
                }}
                className="advoq-input vakalat-select"
              >
                {DOC_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type === 'All' ? 'All document types' : formatDocType(type)}
                  </option>
                ))}
              </select>
            </div>
          </section>

          {isLandingCollapsed && (hiddenCollectionCount > 0 || hiddenDocumentCount > 0) && (
            <section className="landing-collapsed-banner">
              <p className="landing-collapsed-banner__copy">
                <strong>Compact first view.</strong> Showing the drafts that fit cleanly on landing. Open the full desk to browse the remaining {hiddenDocumentCount > 0 ? `${hiddenDocumentCount} draft${hiddenDocumentCount === 1 ? '' : 's'}` : `${hiddenCollectionCount} collections`}.
              </p>
              <button type="button" className="advoq-btn-secondary text-sm" onClick={expandLanding}>
                Open full desk
              </button>
            </section>
          )}

          <section className="vakalat-workspace">
            <aside className="app-shell-panel vakalat-collections" aria-label="Vakalat collections">
              <div className="vakalat-collections__header">
                <p className="vakalat-eyebrow">Collections</p>
                <h2 className="vakalat-collections__title">Draft views</h2>
                <p className="vakalat-collections__description">
                  Move between live drafting, final copies, circulation, and archive without losing context.
                </p>
              </div>

              <div className="vakalat-collections__list">
                {visibleCollectionItems.map((item) => {
                  const collection = STATUS_META[item.key];
                  const isActive = statusFilter === item.key;

                  return (
                    <button
                      key={item.key}
                      type="button"
                      data-testid={`vakalat-collection-${item.key}`}
                      className={`vakalat-collection ${isActive ? 'is-active' : ''}`}
                      onClick={() => {
                        setStatusFilter(item.key);
                        expandLanding();
                      }}
                    >
                      <span className="vakalat-collection__copy">
                        <span className="vakalat-collection__label">{collection.label}</span>
                        <span className="vakalat-collection__description">{collection.description}</span>
                      </span>
                      <span className="vakalat-collection__count">{item.count}</span>
                    </button>
                  );
                })}
              </div>
              {isLandingCollapsed && hiddenCollectionCount > 0 && (
                <button type="button" className="advoq-btn-secondary text-sm vakalat-collections__expand" onClick={expandLanding}>
                  Show {hiddenCollectionCount} more collection{hiddenCollectionCount === 1 ? '' : 's'}
                </button>
              )}
            </aside>

            <section className="app-shell-panel vakalat-results" data-testid="vakalat-results">
              {filtered.length === 0 ? (
                <div className="vakalat-empty">
                  <FileText size={34} />
                  <h2>No drafts match this view</h2>
                  <p>Clear the current filters or open a fresh drafting workspace from a blank file or precedent.</p>
                  <div className="vakalat-empty__actions">
                    <button type="button" onClick={() => setShowNewModal(true)} className="advoq-btn-primary">
                      <Plus size={14} />
                      New Draft
                    </button>
                    <button type="button" onClick={() => navigate('/templates')} className="advoq-btn-ghost">
                      <BookOpen size={14} />
                      Browse Precedents
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="vakalat-results__header">
                    <div>
                      <p className="vakalat-eyebrow">Current view</p>
                      <h2 className="vakalat-results__title">{activeCollection.sectionTitle}</h2>
                    </div>
                    <p className="vakalat-results__description">{activeCollection.description}</p>
                  </div>

                  <div className="vakalat-results__sections">
                    {visibleGroupedSections.map((section) => {
                      const visibleRows = isLandingCollapsed ? section.items.slice(0, landingRowLimit) : section.items;
                      const hiddenRows = Math.max(0, section.items.length - visibleRows.length);

                      return (
                      <section key={section.key} className="vakalat-section" data-testid={`vakalat-section-${section.key}`}>
                        <header className="vakalat-section__header">
                          <div>
                            <h3 className="vakalat-section__title">{section.sectionTitle}</h3>
                            <p className="vakalat-section__description">{section.description}</p>
                          </div>
                          <span className="vakalat-section__count">{section.items.length}</span>
                        </header>

                        <div className="vakalat-table-head" aria-hidden="true">
                          <span>Document</span>
                          <span>Context</span>
                          <span>Activity</span>
                          <span>Actions</span>
                        </div>

                        <div className="vakalat-table-body">
                          {visibleRows.map((doc) => (
                            <article
                              key={doc.doc_id}
                              className="vakalat-row"
                              data-testid={`vakalat-row-${doc.doc_id}`}
                              onClick={() => openDocument(doc.doc_id)}
                            >
                              <div className="vakalat-row__main">
                                <div className="vakalat-row__title-line">
                                  <h4 className="vakalat-row__title">{doc.title || 'Untitled draft'}</h4>
                                  <span className={`vakalat-status-chip vakalat-status-chip--${String(doc.status || 'DRAFT').toLowerCase()}`}>
                                    {STATUS_META[doc.status]?.label || doc.status}
                                  </span>
                                </div>

                                <div className="vakalat-row__meta">
                                  <span className="vakalat-type-pill">{formatDocType(doc.type)}</span>
                                  <span>{getWordCount(doc)} words</span>
                                </div>

                                <p className="vakalat-row__preview">{getPreview(doc)}</p>
                              </div>

                              <div className="vakalat-row__context">
                                <span className="vakalat-row__label">Context</span>
                                <strong>{doc.court || 'General drafting file'}</strong>
                                <span>
                                  {doc.court ? 'Court formatting available on this draft.' : 'No court formatting assigned yet.'}
                                </span>
                              </div>

                              <div className="vakalat-row__activity">
                                <span className="vakalat-row__label">Last touched</span>
                                <strong>{formatRelativeDate(doc.updated_at)}</strong>
                                <span>{formatShortDate(doc.updated_at)}</span>
                              </div>

                              <div className="vakalat-row__actions" onClick={(event) => event.stopPropagation()}>
                                <button
                                  type="button"
                                  className="vakalat-open-button"
                                  onClick={() => openDocument(doc.doc_id)}
                                >
                                  Open
                                  <ArrowUpRight size={14} />
                                </button>
                                <div className="vakalat-row__icon-actions">
                                  <button
                                    type="button"
                                    className="vakalat-icon-button"
                                    onClick={() => openLinkedWorkspace(doc, '/chat')}
                                    title="Open in Chat"
                                    aria-label={`Open ${doc.title} in Chat`}
                                  >
                                    <MessageSquare size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    className="vakalat-icon-button"
                                    onClick={() => openLinkedWorkspace(doc, '/research')}
                                    title="Open in Research"
                                    aria-label={`Open ${doc.title} in Research`}
                                  >
                                    <BookOpen size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    className="vakalat-icon-button vakalat-icon-button--danger"
                                    onClick={() => deleteDocument(doc.doc_id)}
                                    title="Delete"
                                    aria-label={`Delete ${doc.title}`}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            </article>
                          ))}
                        </div>
                        {isLandingCollapsed && hiddenRows > 0 && (
                          <div className="vakalat-section__footer">
                            <p>{hiddenRows} more draft{hiddenRows === 1 ? '' : 's'} in this section.</p>
                            <button type="button" className="advoq-btn-secondary text-sm" onClick={expandLanding}>
                              Open section
                            </button>
                          </div>
                        )}
                      </section>
                      );
                    })}
                  </div>
                </>
              )}
            </section>
          </section>
        </div>
      </main>

      {showNewModal && (
        <div className="app-modal-backdrop">
          <div className="app-modal app-modal--md" data-testid="new-document-modal">
            <div className="app-modal__header">
              <div>
                <h3 className="app-modal__title">New draft</h3>
                <p className="app-modal__description">Start blank or generate a first pass from a clear chamber brief.</p>
              </div>
            </div>
            <div className="app-modal__body space-y-4">
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>
                  Document Type
                </label>
                <select value={newDocType} onChange={(event) => setNewDocType(event.target.value)} className="advoq-input w-full text-sm">
                  {DOC_TYPES.filter((type) => type !== 'All').map((type) => (
                    <option key={type} value={type}>
                      {formatDocType(type)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>
                  Title or drafting brief
                </label>
                <input
                  data-testid="new-doc-title"
                  value={newDocTitle}
                  onChange={(event) => setNewDocTitle(event.target.value)}
                  className="advoq-input w-full text-sm"
                  placeholder="e.g. Anticipatory bail petition for a director facing custodial threat"
                />
              </div>
            </div>
            <div className="app-modal__footer">
              <button type="button" onClick={() => setShowNewModal(false)} className="advoq-btn-ghost flex-1 text-sm">
                Cancel
              </button>
              <button type="button" data-testid="create-blank-doc" onClick={createDocument} className="advoq-btn-ghost flex-1 text-sm">
                Create Blank
              </button>
              <button
                type="button"
                data-testid="generate-with-ai"
                onClick={generateDocument}
                disabled={generating}
                className="advoq-btn-primary flex-1 text-sm"
              >
                {generating ? 'Generating...' : 'Generate First Pass'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
